import React from 'react';
import { Text, Card } from './common';
import { format, subDays, isSameDay, parseISO } from 'date-fns';
import { PracticeSession } from '../api/services/practice';

interface StreakDisplayProps {
  currentStreak: number;
  lastPracticeDate: string;
  practiceHistory: PracticeSession[];
}

const StreakDisplay: React.FC<StreakDisplayProps> = ({
  currentStreak,
  lastPracticeDate,
  practiceHistory,
}) => {

  // Generate last 30 days for calendar
  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const date = subDays(new Date(), i);
    return format(date, 'yyyy-MM-dd');
  }).reverse();

  // Check if a date has practice
  const hasPractice = (date: string) => {
    return practiceHistory.some(session => {
      const sessionDate = parseISO(session.start_time);
      return isSameDay(sessionDate, new Date(date));
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Practice Calendar */}
        <Card className="p-6 flex flex-col">
          <Text variant="h3" className="mb-4">Practice Calendar</Text>
          <div className="grid grid-cols-7 gap-2 w-full">
            {last30Days.map((date, index) => {
              const practiced = hasPractice(date);
              return (
                <div
                  key={date}
                  className={`
                    aspect-square rounded flex items-center justify-center text-xs
                    ${practiced
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                    }
                  `}
                >
                  {format(new Date(date), 'd')}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Streak Milestones */}
        <Card className="p-6 flex flex-col">
          <Text variant="h3" className="mb-4">Streak Milestones</Text>
          <div className="space-y-3 flex-grow">
            {[7, 30, 100].map((milestone) => {
              const progress = Math.min(currentStreak / milestone * 100, 100);
              const isAchieved = currentStreak >= milestone;
              return (
                <div
                  key={milestone}
                  className={`
                    flex flex-col p-3 rounded-lg
                    ${isAchieved
                      ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                    }
                  `}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Text variant="p" className="font-medium">{milestone} Day Streak</Text>
                      <Text variant="p" className="text-sm">
                        {isAchieved ? 'Achieved!' : `${milestone - currentStreak} days to go`}
                      </Text>
                    </div>
                    {isAchieved && (
                      <span className="text-2xl">🏆</span>
                    )}
                  </div>
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        isAchieved
                          ? 'bg-emerald-500 dark:bg-emerald-400'
                          : 'bg-indigo-500 dark:bg-indigo-400'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StreakDisplay;